import {LuckyDrawFormProps} from './LuckyDrawForm.types'
import {
  Alert,
  Button,
  ControlLabel,
  DialogRef,
  Divider,
  Radio,
  Stack,
  Typography,
} from 'src/app/components'
import {useLuckyDrawForm1} from './LuckyDrawForm1.hooks'
import {
  HookFormProvider,
  InputDatePicker,
  InputImageUpload,
  InputRadioGroup,
  InputSelect,
  InputTextEditor,
  InputTextField,
} from 'src/app/components/HookForm'
import {DateObject} from 'react-multi-date-picker'
import dayjs from 'dayjs'
import InputSelectMall from 'src/app/components/HookForm/InputSelect/InputSelect.mall'
import {usePermissions} from 'src/app/hooks/permissions-hook'
import {
  isPrintDateTypeLockedOnEdit,
  LuckyDrawModel,
  PRINT_DATE_TYPE_LABELS,
  PRINT_DATE_TYPE_OPTIONS,
  resolvePrintDateType,
  validatePrintCoupon,
  validatePrintDateTypeField,
} from '../../../models/LuckyDraw.model'
import {useEffect, useRef} from 'react'
import {Refs} from 'src/app/utils/refs-utils'
import {getErrorMessage} from 'src/app/utils/api-utils'

const formatImagePayload = (img?: string) => {
  if (!img) return undefined
  if (img.includes('base64,')) {
    return img.split('base64,', 2)[1]
  }
  return img.startsWith('http') ? undefined : img
}

const applyPrintDateTypePayload = (
  payload: LuckyDrawModel,
  formData: LuckyDrawModel & { print_date_type?: unknown },
  fallback?: LuckyDrawModel,
) => {
  const printDateType = resolvePrintDateType(formData.print_date_type)
  payload.print_date_type = printDateType
  if (printDateType === 'MAX_DAY_TO_PRINT') {
    payload.max_day_to_print =
      formData.max_day_to_print && formData.max_day_to_print > 0
        ? Number(formData.max_day_to_print)
        : fallback?.max_day_to_print
    delete payload.last_print_date
  } else if (printDateType === 'FIX_DATE') {
    payload.last_print_date = formData.last_print_date || fallback?.last_print_date
    delete payload.max_day_to_print
  } else {
    delete payload.max_day_to_print
    delete payload.last_print_date
  }
}

export function LuckyDrawForm1(props: Readonly<LuckyDrawFormProps>) {
  const { id, currentDetail, doPreventExit, save, saveDraft, initialPrintDateType } = props
  const alertSaveAsDraftRef = useRef<DialogRef>(null)
  const permission = usePermissions()
  const state = useLuckyDrawForm1(id, currentDetail)
  const campaignStatus = state.luckyDrawDetail?.data?.status ?? currentDetail?.status ?? ''
  const isEdit = !!id && ['PENDING', 'ACTIVE'].includes(campaignStatus)
  useEffect(() => {
    if (state.isFormFilled) {
      doPreventExit && doPreventExit(!!state.isFormFilled)
    }
  }, [state.isFormFilled, doPreventExit])

  const saveAsDraft = (key: string) => {
    alertSaveAsDraftRef.current?.hide()
    const formData = state.form.getValues()
    const hasPrintable = formData.is_printable !== undefined && formData.is_printable !== null && (formData.is_printable as any) !== ''
    const isPrintableVal = hasPrintable
      ? formData.is_printable === true || (formData.is_printable as unknown) === 'true'
      : undefined
    const couponSelectionImageBase64 = formatImagePayload(formData.coupon_selection_image)

    const payload: LuckyDrawModel = {
      ...formData,
      directory_ids: formData.directories?.map((_) => _?.id),
      category: 'LUCKY_DRAW',
      is_draft: true,
      is_printable: isPrintableVal,
      max_day_to_choose_prize:
        formData.max_day_to_choose_prize && formData.max_day_to_choose_prize > 0
          ? formData.max_day_to_choose_prize
          : undefined,
      image: formatImagePayload(formData.image),
      coupon_selection_image: couponSelectionImageBase64,
    }
    if (isPrintableVal) {
      applyPrintDateTypePayload(payload, formData, currentDetail)
    } else {
      delete payload.print_date_type
      delete payload.max_day_to_print
      delete payload.last_print_date
    }
    if (!hasPrintable) {
      delete payload.is_printable
    }
    if (isEdit) {
      payload.id = id
      delete payload.thumbnail_image
    }
    delete payload.campaign_type
    delete payload.directories
    hitPostAPI(payload, key)
  }

  const submitButtonClick = state.form.handleSubmit(
    async ({directories, category_type, image, coupon_selection_image, ...formData}) => {
      const isPrintableVal = formData.is_printable === true || (formData.is_printable as unknown) === 'true'
      const couponSelectionImageBase64 = formatImagePayload(coupon_selection_image)

      const payload: LuckyDrawModel = {
        ...formData,
        directory_ids: directories?.map((_) => _?.id),
        category: 'LUCKY_DRAW',
        is_printable: isPrintableVal,
        image: formatImagePayload(image),
        coupon_selection_image: couponSelectionImageBase64,
      }

      if (isPrintableVal) {
        applyPrintDateTypePayload(payload, formData, currentDetail)
      } else {
        delete payload.print_date_type
        delete payload.max_day_to_print
        delete payload.last_print_date
      }

      if (isEdit) {
        payload.id = id
        delete payload.thumbnail_image
      } else {
        payload.is_draft = true
      }
      hitPostAPI(payload, 'SUBMIT')
    }
  )

  const hitPostAPI = async (payload: LuckyDrawModel, key: string) => {
    try {
      const result = await state.saveLuckyDrawGeneral.mutateAsync(payload)
      const response_output = result.data.response_output
      const contentDetail = response_output?.detail
      const detailToPass = { ...payload, ...contentDetail }
      if (resolvePrintDateType(payload.print_date_type) === 'MAX_DAY_TO_PRINT') {
        delete detailToPass.last_print_date
      } else if (resolvePrintDateType(payload.print_date_type) === 'FIX_DATE') {
        detailToPass.last_print_date =
          detailToPass.last_print_date || payload.last_print_date || currentDetail?.last_print_date
      }
      if (key === 'SUBMIT') {
        save && save(detailToPass)
      } else {
        saveDraft && saveDraft(key, detailToPass)
      }
    } catch (error: any) {
      if (error?.response?.data?.response_schema?.response_code === 'PAKUWON-400')
        Refs.pageToast.show({
          severity: 'danger',
          message: `${getErrorMessage(error, true)?.en}`,
        })
    }
  }
  const saveAndExit = () => {
    const {status} = state.luckyDrawDetail.data
    const isPendingOrActive = status === 'PENDING' || status === 'ACTIVE'

    doPreventExit?.(false)
    isPendingOrActive ? saveWithTrigger() : saveAsDraft('STAY')
  }

  const saveWithTrigger = state.form.handleSubmit(() => saveAsDraft('STAY'))

  const isPrintable = state.form.watch('is_printable') === true || (state.form.watch('is_printable') as unknown) === 'true'
  const printDateType = resolvePrintDateType(state.form.watch('print_date_type'))
  const isPrintDateTypeDisabled = isPrintDateTypeLockedOnEdit(campaignStatus, initialPrintDateType)

  return (
    <>
      <HookFormProvider
        enables={state.enabledFields()}
        query={state.luckyDrawDetail}
        form={state.form}
      >
        <Stack className='p-6'>
          <ControlLabel label='Campaign Type'>
            <InputSelect
              onChangeValue={() => {}}
              placeholder='Select campaign type'
              isMulti={false}
              options={state.masterCampaignType}
              rule={{required: false}}
              form={state.form}
              disabled={true}
              defaultValue={{label: 'Lucky Draw', value: 'LUCKY_DRAW'}}
              name='category_type'
              selectOption={(_) => ({
                label: _.label,
                value: _.value,
              })}
            />
          </ControlLabel>
          <ControlLabel label='Campaign Name'>
            <InputTextField
              placeholder='Enter campaign name'
              rule={{required: true}}
              name='name'
              maxLength={99}
            />
          </ControlLabel>

          <ControlLabel label='Mall'>
            <InputSelectMall
              placeholder='Type to add'
              isMulti={true}
              form={state.form}
              rule={{required: true}}
            />
          </ControlLabel>

          <ControlLabel label='Periode' required={true}>
            <Stack direction='row' items='center'>
              <InputDatePicker
                placeholder='Select start date'
                minDate={state.minPeriod.minStartDate}
                rule={{required: true}}
                name='start_date'
                form={state.form}
              />
              <Divider className='w-[50px] border-t-2' color='primary' />
              <InputDatePicker
                placeholder='Select end date'
                minDate={state.minPeriod.minEndDate}
                rule={{required: true}}
                name='end_date'
                form={state.form}
                maxDate={new DateObject().add(5, 'year')}
              />
            </Stack>
          </ControlLabel>
          <ControlLabel label='Print Coupon' required={true}>
            <InputRadioGroup
              form={state.form}
              name='is_printable'
              rule={{
                validate: validatePrintCoupon,
              }}
            >
              <Radio key={'is_printable_true'} label='Yes' value={'true'} />
              <Radio key={'is_printable_false'} label='No' value={'false'} />
            </InputRadioGroup>
          </ControlLabel>
          <Stack className={isPrintable ? undefined : 'hidden'}>
              <ControlLabel label='Print Date Type' required={true}>
                <InputSelect
                  placeholder='Select print date type'
                  isMulti={false}
                  options={PRINT_DATE_TYPE_OPTIONS}
                  rule={{
                    validate: (value) => validatePrintDateTypeField(value, state.form.getValues('is_printable')),
                  }}
                  form={state.form}
                  disabled={isPrintDateTypeDisabled}
                  name='print_date_type'
                  selectOption={(_) => ({
                    label: _.label,
                    value: _.value,
                  })}
                />
              </ControlLabel>
              <div className={printDateType === 'MAX_DAY_TO_PRINT' ? undefined : 'hidden'}>
                <ControlLabel label={PRINT_DATE_TYPE_LABELS.MAX_DAY_TO_PRINT} required={true}>
                  <InputTextField
                    placeholder='Enter max days to print after'
                    name='max_day_to_print'
                    type='number'
                    suffix='Days'
                    rule={{
                      validate: (val) => {
                        const printable = state.form.getValues('is_printable') === true || (state.form.getValues('is_printable') as unknown) === 'true'
                        const currentPrintDateType = resolvePrintDateType(state.form.getValues('print_date_type'))
                        if (!printable || currentPrintDateType !== 'MAX_DAY_TO_PRINT') return true
                        if (val === undefined || val === null || val === '') return 'Field cannot be empty'
                        const days = Number(val)
                        if (days < 1) return 'Minimum day is 1'
                        if (days > 999) return 'Maximum day is 999'
                        return true
                      },
                    }}
                  />
                </ControlLabel>
              </div>
              <div className={printDateType === 'FIX_DATE' ? undefined : 'hidden'}>
                <ControlLabel label={PRINT_DATE_TYPE_LABELS.FIX_DATE} required={true}>
                  <InputDatePicker
                    placeholder='Select last print date'
                    name='last_print_date'
                    form={state.form}
                    minDate={new DateObject()}
                    maxDate={new DateObject(dayjs().add(1, 'month').endOf('month').toDate())}
                    rule={{
                      validate: (val: string) => {
                        const printable = state.form.getValues('is_printable') === true || (state.form.getValues('is_printable') as unknown) === 'true'
                        const currentPrintDateType = resolvePrintDateType(state.form.getValues('print_date_type'))
                        if (!printable || currentPrintDateType !== 'FIX_DATE') return true
                        if (!val) return 'Field cannot be empty'
                        const selected = dayjs(val)
                        const today = dayjs().startOf('day')
                        if (selected.isBefore(today)) {
                          return 'Cannot be selected as a past date'
                        }
                        const maxAllowed = dayjs().add(1, 'month').endOf('month')
                        if (selected.isAfter(maxAllowed)) {
                          return 'Date cannot be after the end of the following month'
                        }
                        return true
                      },
                    }}
                  />
                </ControlLabel>
              </div>
          </Stack>
          <ControlLabel label='Max Days to select coupon after Snap Receipt ' required={true}>
            <InputTextField
              placeholder='Enter max days to print after'
              name='max_day_to_choose_prize'
              type='number'
              suffix='Days'
              rule={{
                required: true,
                min: {
                  value: 1,
                  message: 'Minimum day is 1',
                },
                max: {
                  value: 999,
                  message: 'Maximum day is 999',
                },
              }}
            />
          </ControlLabel>
          <ControlLabel label='Consolation Name in PG Card Apps'>
            <InputTextField
              placeholder='Enter consolation name'
              rule={{required: false}}
              name='consolation_alias'
              maxLength={99}
            />
          </ControlLabel>
          <ControlLabel label='Consolation Image' required={true}>
            <div className='grid grid-cols-2 gap-8'>
              <InputImageUpload
                placeholder='Upload Photo (JPG/PNG)'
                maxSize={Math.pow(1024, 2) * 5}
                accept={['.jpg', '.jpeg', '.png']}
                rule={{required: true}}
                form={state.form}
                name='image'
              />
              <div className='flex flex-col justify-center'>
                <ul className='text-xs text-gray-500 list-disc list-inside dark:text-gray-400'>
                  <li className='mb-4'>
                    Size: Max size of file is <strong>5 MB</strong>.
                  </li>
                  <li className='mb-4'>
                    Photo ratio is <strong>16:9</strong>
                  </li>
                  <li>
                    File format: only allow <strong>.jpg / .png</strong>
                  </li>
                </ul>
              </div>
            </div>
          </ControlLabel>
          <ControlLabel label='Campaign Image' required={true}>
            <div className='grid grid-cols-2 gap-8'>
              <InputImageUpload
                placeholder='Upload Photo (JPG/PNG)'
                maxSize={Math.pow(1024, 2) * 5}
                accept={['.jpg', '.jpeg', '.png']}
                rule={{required: true}}
                form={state.form}
                name='coupon_selection_image'
              />
              <div className='flex flex-col justify-center'>
                <ul className='text-xs text-gray-500 list-disc list-inside dark:text-gray-400'>
                  <li className='mb-4'>
                    Size: Max size of file is <strong>5 MB</strong>.
                  </li>
                  <li className='mb-4'>
                    Photo ratio is <strong>16:9</strong>
                  </li>
                  <li>
                    File format: only allow <strong>.jpg / .png</strong>
                  </li>
                </ul>
              </div>
            </div>
          </ControlLabel>
          <ControlLabel label='Terms & Conditions (EN)'>
            <div>
              <InputTextEditor
                height={150}
                form={state.form}
                name='tnc_eng'
                placeholder='Enter Event Description'
              />
            </div>
          </ControlLabel>
          <ControlLabel label='Terms & Conditions (ID)'>
            <div>
              <InputTextEditor
                height={150}
                form={state.form}
                name='tnc_idn'
                placeholder='Enter Event Description'
              />
            </div>
          </ControlLabel>
        </Stack>
        <Stack className={'px-8 py-6 border-t border-neutral-200'} direction='row' gap={4}>
          {permission.setVisibleByAccess(
            ['manage_campaign_lucky_draw.create', 'manage_campaign_lucky_draw.update'],
            <Button
              disabled={!state.isFormFilled}
              variant='outlined'
              loading={state.saveLuckyDrawGeneral.isLoading}
              onClick={() => {
                isEdit ? saveAndExit() : alertSaveAsDraftRef.current?.show()
              }}
            >
              {'Save' + (!isEdit ? ' as Draft' : '')}
            </Button>
          )}

          <Stack direction='row' justify='end' gap={4}>
            {permission.setVisibleByAccess(
              ['manage_campaign_lucky_draw.create', 'manage_campaign_lucky_draw.update'],
              <Button
                disabled={!state.isFormFilled}
                loading={state.saveLuckyDrawGeneral.isLoading}
                onClick={() => {
                  submitButtonClick()}}
              >
                Next
              </Button>
            )}
          </Stack>
        </Stack>
      </HookFormProvider>
      <Alert
        cancelText='Go to List'
        submitText='Stay on page'
        onCancel={() => saveAsDraft('BACK')}
        onSubmit={() => saveAsDraft('STAY')}
        title='Save as Draft?'
        ref={alertSaveAsDraftRef}
        closeOnBackdropClicked={false}
      >
        <Typography>
          Your progress will be saved as a draft. You can continue editing later.
          <br />
          Would you like to stay on this page or return to the list?
        </Typography>
      </Alert>
    </>
  )
}
